import { PlayerConfig, EtvConfig, AvailableLanguages, Clip } from '@/interfaces/player-config.interface';

import { etvService } from '../services/etv.service';
import { logService, LogLevel } from '../services/log.service';

export class ConfigService {
  private customStyleAdded: boolean = false;

  private playerConfig: PlayerConfig = {
    live: false,
    parentHost: '',
    parentFullUrl: '',
    publisherId: '',
    sourceId: '',
    token: '',
    language: 'en',
    mute: true,
    previewImage: '',
    autoplay: false,
    accountId: '',
    advertisement: {
      enabled: false,
      midroll: 0,
      adserver: '',
    },
    overlay: false,
    adTargetTag: '',
  };

  public async getConfig(parentHost: string, parentFullUrl: string, userConfig: any, etvConfig: EtvConfig): Promise<PlayerConfig> {
    this.playerConfig.parentHost = parentHost;
    this.playerConfig.parentFullUrl = parentFullUrl;
    this.playerConfig.token = userConfig.token;

    // live stream config ################################################

    if (etvConfig.streamUris && etvConfig.streamUris.hls) {
      this.playerConfig.stream = etvConfig.streamUris.hls;

      /*
       * Provide embedding website domain as header
       * https://develop.earthtv.com/jira/browse/WV-47
       */
      this.playerConfig.stream += '&domain=' + parentHost;
      this.playerConfig.live = true;

      // live location
      if (etvConfig.template.overlay && etvConfig.template.overlay.enabled && etvConfig.source && etvConfig.source.location) {
        this.playerConfig.location = etvConfig.source.location;
      }
    }

    // playlist stream config ################################################
    // https://github.com/silvermine/videojs-quality-selector
    if (etvConfig.playlist) {
      const response = await etvService.getPlaylist(
        etvConfig.playlist.id,
        userConfig.token,
        userConfig.query,
        userConfig.sortField,
        userConfig.sortOrder,
        userConfig.limit,
        userConfig.offset
      );

      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      let previewWidth = 640;
      let previewHeight = 360;
      if (windowHeight < 360) {
        previewWidth = windowWidth;
        previewHeight = windowHeight;
      }

      const playlist = response.data.results.map((clip: Clip) => {
        return {
          sources: this.getVariants(clip),
          poster: `${clip.medium.mediaUrls.previewImage.cdn}&w=${previewWidth}&h=${previewHeight}&q=70`,
          location: clip.source.location,
          source: {
            id: clip.source.id,
            alias: clip.source.alias,
          },
          time: clip.medium.startTime,
        };
      });

      this.playerConfig.playlist = playlist.filter((clip: any) => {
        return clip.sources !== undefined; // remove undefined clips
      });

      this.playerConfig.live = false;

      if (userConfig.max_duration) {
        if (userConfig.max_duration >= (process.env.VUE_APP_CLIP_MAX_DURATION_MIN || 1)) {
          this.playerConfig.clipMaxDuration = userConfig.max_duration;
        } else {
          logService.warn(`max_duration config of ${userConfig.max_duration}s ignored. Must be at least ${process.env.VUE_APP_CLIP_MAX_DURATION_MIN || 1}s`);
        }
      }
    }

    // general config ################################################

    /**
     * Use language for url param
     * if not defined use default en
     */
    // @ts-ignore
    if (userConfig.language && AvailableLanguages[userConfig.language]) {
      this.playerConfig.language = userConfig.language;
    } else {
      this.playerConfig.language = AvailableLanguages.en;
    }

    // playback
    if (etvConfig.template.playback) {
      // mute
      this.playerConfig.mute = userConfig.mute ? JSON.parse(userConfig.mute) : etvConfig.template.playback.mute;

      if (etvConfig.template.playback.limit) {
        this.playerConfig.stopLiveStreamInSec = etvConfig.template.playback.limit; // in seconds
      }

      // https://develop.earthtv.com/jira/browse/WV-21
      this.playerConfig.autoplay = userConfig.autoplay ? JSON.parse(userConfig.autoplay) : etvConfig.template.playback.autoplay;
      if (this.playerConfig.autoplay) {
        // mute player if autoplay
        this.playerConfig.mute = true;
      }
    }

    if (userConfig.previewImage) {
      this.playerConfig.previewImage = userConfig.previewImage;
    } else {
      this.playerConfig.previewImage = this.getPreviewImage(etvConfig);
    }
    // Overlay
    if (etvConfig.template.overlay) {
      this.playerConfig.overlay = etvConfig.template.overlay.enabled || false;
    }

    if (etvConfig.template.accountId) {
      this.playerConfig.accountId = etvConfig.template.accountId;
    }

    // Custom Style
    if (etvConfig.template.assets && etvConfig.template.assets.css) {
      this.addCustomStyle(etvConfig.template.assets.css.urls.cdn);
    }

    // Custom logo
    if (etvConfig.template.assets && etvConfig.template.assets.logo && etvConfig.template.assets.logo.urls.cdn) {
      this.playerConfig.logo = etvConfig.template.assets.logo.urls.cdn;
    }

    // advertisement
    // Enable ads if any ad type is enabled (preroll, midroll, or postroll)
    const adConfig = etvConfig.template.advertisement;
    console.log('[ConfigService] Advertisement config from EtvConfig:', adConfig);

    if (adConfig && adConfig.adserver) {
      const hasAnyAdEnabled =
        (adConfig.preroll && adConfig.preroll.enabled) ||
        (adConfig.midroll && adConfig.midroll.enabled) ||
        (adConfig.postroll && adConfig.postroll.enabled);

      console.log('[ConfigService] Ad types enabled check:', {
        preroll: adConfig.preroll?.enabled,
        midroll: adConfig.midroll?.enabled,
        postroll: adConfig.postroll?.enabled,
        hasAnyAdEnabled
      });

      if (hasAnyAdEnabled) {
        this.playerConfig.advertisement.enabled = true;
        this.playerConfig.advertisement.adserver = adConfig.adserver;
        console.log('[ConfigService] Enabled ads - adserver:', adConfig.adserver);

        // midrolls: https://earthtv.atlassian.net/browse/WV-109
        if (
          adConfig.midroll &&
          adConfig.midroll.enabled &&
          adConfig.midroll.interval > 0 &&
          !this.playerConfig.live // activate midrolls only for clips, not live
        ) {
          this.playerConfig.advertisement.midroll = adConfig.midroll.interval;
        }
      } else {
        console.log('[ConfigService] No ad types enabled, keeping ads disabled');
      }
    } else {
      console.log('[ConfigService] No advertisement config or adserver found');
    }

    if (etvConfig.publisher && etvConfig.publisher.id) {
      this.playerConfig.publisherId = etvConfig.publisher.id;
    }

    if (etvConfig.source && etvConfig.source.id) {
      this.playerConfig.sourceId = etvConfig.source.id;
    }

    if (userConfig.adTargetTag) {
      this.playerConfig.adTargetTag = userConfig.adTargetTag;
      logService.trace('Set adTargetTag to player config: ' + this.playerConfig.adTargetTag);
    }

    // https://develop.earthtv.com/jira/browse/WV-46
    if (etvConfig.source && etvConfig.source.maintenance) {
      this.playerConfig.maintenance = true;
    }

    return this.playerConfig;
  }

  public async validateQuery(query: any) {
    if (query.token) {
      return true;
    } else {
      return false;
    }
  }

  private getVariants(clip: Clip) {
    let variants = [];
    const orig = clip.medium.sourceVariantId;
    let index = 0;
    let selectedIndex = 0;
    let previousBitrate = 0;

    // prepare variants
    for (const variant of clip.variants) {
      // WV-112: ignore videos types
      const ignore = ['video/mpeg-ps', 'video/MP2P', 'video/mp2p'];
      if (ignore.includes(variant.contentType)) {
        continue;
      }

      let label = variant.videoHeight + 'p';
      if (variant.sourceVariantId) {
        label += ' original';
      } else if (variant.id === orig) {
        label += ' original';
      } else {
        label += ' ' + (variant.videoBitrate / 1000000).toFixed(2) + ' MBit';
      }

      if (variant.videoHeight === 720 && variant.videoBitrate > previousBitrate) {
        selectedIndex = index;
        previousBitrate = variant.videoBitrate;
      }

      const src = {
        src: variant.urls.cdn,
        type: 'video/mp4',
        bitrate: variant.videoBitrate,
        hight: variant.videoHeight,
        label,
        selected: false,
      };
      variants.push(src);
      index++;
    }

    // clips has no valid variants
    if (variants.length <= 0) {
      return undefined;
    }

    variants[selectedIndex].selected = true;
    // sort by bitrate
    variants = [...variants].sort((a, b) => b.bitrate - a.bitrate);

    return variants;
  }

  private addCustomStyle(path: string) {
    if (!this.customStyleAdded) {
      this.customStyleAdded = true;
      const link = document.createElement('link');
      link.href = path;
      link.type = 'text/css';
      link.rel = 'stylesheet';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }

  private getPreviewImage(config: EtvConfig) {
    if (config.stream) {
      if (config.stream.mediaUrls.previewImage.cdn) {
        return config.stream.mediaUrls.previewImage.cdn;
      } else if (config.stream.mediaUrls.previewImage.edge) {
        return config.stream.mediaUrls.previewImage.edge;
      } else {
        return ''; // TODO: add default preview image
      }
    } else {
      if (this.playerConfig.playlist && this.playerConfig.playlist.length) {
        // @ts-ignore
        return this.playerConfig.playlist[0].poster ? this.playerConfig.playlist[0].poster : '';
      } else {
        return '';
      }
    }
  }
}

export const configService = new ConfigService();
